import styled, { keyframes } from "styled-components";
import { colorsPalettes, setElevation, setFontVariant } from "@similarweb/styles";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

const { black, blue, carbon } = colorsPalettes;

export const StepWrapper = styled(Stack)`
    align-items: center;
    padding-top: 24px;
`;

/* Figma node 141:4390 — the compose screen sits directly on the page's blue-grey canvas (no
   white card background), one 40px-gapped column: heading, the segment-budget notice (when
   shown), the composer, then the suggestions group — which itself ends with the manual-
   fallback button, so no bottom padding is needed here beyond that group's own last child. No
   top padding either: `MarketBuilder.tsx`'s own page header (with its "Create a custom
   industry" title) already sits above this, plus `StepWrapper`'s own 24px — stacking a further
   64px here on top of that pushed the card's content much further down than intended. */
export const StepCard = styled(Flex)`
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 1180px;
    padding: 0 120px;
`;

export const StepHeading = styled.p`
    ${setFontVariant("h3", carbon[500])}
    text-align: center;
    margin: 0;
`;

/* Figma node 141:4390 — one pill holding the textarea and, below it, the country chip and
   submit button. ai-kit's `JumboComposer` paints that chrome on an internal node it exposes
   no prop for and centers itself absolutely in a full-height page, so the card owns the chrome
   and the textarea here instead. */
export const ComposerCard = styled(Stack)`
    width: 720px;
    max-width: 100%;
    box-sizing: border-box;
    background: ${carbon[0]};
    border: 1px solid ${carbon[100]};
    border-radius: 24px;
    ${setElevation("Z2")}
    padding: 16px;
    gap: 8px;
`;

/* Grows with its content up to the height ai-kit's own composer stops at, then scrolls. */
export const ComposerTextArea = styled.textarea`
    ${setFontVariant("body2", carbon[400])}
    width: 100%;
    box-sizing: border-box;
    border: none;
    outline: none;
    background: transparent;
    resize: none;
    max-height: 76px;
    overflow-y: auto;
`;

export const ComposerCountryRow = styled(Flex)`
    align-items: center;
    justify-content: space-between;
`;

/* Figma node 141:4404 — Body1/carbon-400, not caption text. */
export const SuggestionsHint = styled.p`
    ${setFontVariant("body1", carbon[400])}
    margin: 0;
`;

/* Figma node 141:4403's own 24px gap between the hint, the chip rows, and the "or" divider. */
export const SuggestionsGroup = styled(Stack)`
    align-items: center;
    gap: 24px;
    width: 100%;
`;

export const SuggestionsRow = styled(Flex)`
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    max-width: 704px;
`;

/* Figma node 141:4412 — same Body1/carbon-400 as the hint above it, separating the chip
   suggestions from the manual-fallback button below. */
export const SuggestionsOrDivider = styled.p`
    ${setFontVariant("body1", carbon[400])}
    margin: 0;
`;

/* Figma node 141:4418's two AI-gradient panel backgrounds — neither stop is a `colorsPalettes`
   token nor one of ai-kit's own `--aibo-ref-color-ai-*` variables (those resolve much darker),
   so they're named constants here instead. */
const AI_GRADIENT_LIGHT = "linear-gradient(89.5deg, #EBF1FE 0.82%, #EAFBF7 99.6%)";

/* The gradient checkmark's far stop (Figma node 50:2759). Its near stop is `blue[400]`; this
   teal has no `colorsPalettes` token, so it is named here for the same reason as above. */
export const AI_GRADIENT_CHECK_TEAL = "#2AD3AB";

/* Figma node 141:4418 — the running screen splits into two equal columns (composer / live
   progress) instead of the compose screen's single centered column. Stacks below its own
   width so the split never has to squeeze narrower than a composer card can read. */
export const RunLayout = styled(Flex)`
    gap: 40px;
    align-items: stretch;
    width: 100%;
    max-width: 1180px;

    @media (max-width: 1180px) {
        flex-direction: column;
        align-items: center;
    }
`;

export const RunColumn = styled(Flex)`
    flex: 1 1 0;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 40px;
    padding: 40px;
    box-sizing: border-box;
`;

/* The right-hand column additionally carries the gradient card chrome. Figma node 141:5986 is
   roughly 71% of the design frame's own height (728 of 1024) — a fixed, viewport-relative
   height (capped for very short/tall windows) so the panel reads as a stable card regardless of
   how many steps are expanded, instead of growing every time a step's detail opens. Content
   that outgrows it scrolls inside `RunProgressScrollArea` rather than stretching the card. */
export const RunProgressPanel = styled(RunColumn)`
    border-radius: 24px;
    background: ${AI_GRADIENT_LIGHT};
    height: 70vh;
    min-height: 420px;
    max-height: 720px;
    overflow: hidden;
`;

/* The scrollable middle of `RunProgressPanel`: the optional over-budget notice and the step
   checklist. `EstimatedTimeText` sits outside this area (a sibling, not a child) so it stays
   pinned to the panel's bottom edge instead of scrolling away with the steps. */
export const RunProgressScrollArea = styled(Stack)`
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    align-items: center;
    gap: 40px;
    overflow-y: auto;
`;

/* Figma node 103:3781 — the live composer echoed back while the agent runs: same pill shape as
   `ComposerCard`, with a blue border to read as "active". */
export const RunComposerCard = styled(Flex)`
    width: 100%;
    box-sizing: border-box;
    background: ${carbon[0]};
    border: 1px solid ${blue[400]};
    border-radius: 24px;
    ${setElevation("Z2")}
    padding: 16px;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
`;

export const RunComposerText = styled.p`
    ${setFontVariant("body1", carbon[200])}
    line-height: 24px;
    margin: 0;
    width: 100%;
    overflow-wrap: break-word;
`;

/* `AIButton` (ai-kit) has no `data-${string}` index signature, so its automation id lives on
   this thin wrapper instead of the button itself. */
export const StopButtonWrapper = styled.span`
    display: inline-flex;
    flex: 0 0 auto;
`;

export const EstimatedTimeText = styled.p`
    ${setFontVariant("body1", carbon[300])}
    text-align: center;
    margin: 0;
    flex-shrink: 0;
`;

export const MinimizeRow = styled(Flex)`
    justify-content: center;
    padding: 40px 0 0;
    width: 100%;
`;

/* Both the checklist and its subtext/actions row are capped to the composer's own width (720px)
   so the whole "generating" column reads as one consistent width, not stretched to the wider
   card. */
export const StepChecklistColumn = styled(Flex)`
    width: 360px;
    max-width: 100%;
`;

/* Figma nodes 161:6474 and 224:8231 — the compose screen's notices (the segment-budget
   `PromoBanner`, the rejection `AlertBanner`) take the composer's width. Both banners default to
   `width: 100%`, which already matches the running screen's composer since both are equal-width
   children of the same column — only the compose screen needs pinning down, since there a notice
   is a direct child of the unconstrained page canvas rather than a narrower card. */
export const ComposerWidthNotice = styled.div`
    width: 720px;
    max-width: 100%;
`;

/* Figma node 50:2759 — each checklist row reserves a 40px icon slot (checkmark once done,
   spinner while active, empty otherwise) so label text lines up at the same x regardless of
   status. */
export const StepIconSlot = styled(Flex)`
    width: 40px;
    height: 40px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
`;

export type StepRowStatus = "done" | "active" | "skipped" | "pending";

export const StepLabel = styled.span<{ $status: StepRowStatus }>`
    ${({ $status }) => {
        if ($status === "skipped") {
            return setFontVariant("body1", carbon[250]);
        }
        if ($status === "pending") {
            return setFontVariant("body1", carbon[500]);
        }
        return setFontVariant("body1", black[0]);
    }}
`;

/* The row header (icon + label + trailing chevron/badge) is the click target for toggling a
   done step's detail open/closed. */
export const StepRowHeader = styled(Flex)<{ $clickable?: boolean }>`
    align-items: center;
    gap: 4px;
    ${({ $clickable }) => $clickable && "cursor: pointer;"}
`;

export const ChevronRotator = styled.span<{ $expanded: boolean }>`
    display: inline-flex;
    transition: transform 150ms ease;
    transform: rotate(${({ $expanded }) => ($expanded ? 180 : 0)}deg);
`;

const shimmer = keyframes`
    to {
        background-position: -200% 0;
    }
`;

/* The current step's label sweeps a white highlight through the black text — a plain
   monochrome shimmer, not a colored gradient. */
export const ShimmerLabel = styled.span`
    ${setFontVariant("body1")}
    background-image: linear-gradient(
        110deg,
        ${black[0]} 40%,
        ${carbon[0]} 50%,
        ${black[0]} 60%
    );
    background-size: 200% 100%;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
    animation: ${shimmer} 2s linear infinite;
`;

const spin = keyframes`
    to {
        transform: rotate(360deg);
    }
`;

/* A plain Carbon/200 ring with one gap, rotating — "the loading indicator that will turn
   around" for whichever step is currently in progress. */
export const StepLoaderRing = styled.div`
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: conic-gradient(${carbon[200]} 0deg 300deg, transparent 300deg 360deg);
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px));
    mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px));
    animation: ${spin} 1s linear infinite;
`;

/* Holds nothing but the gradient def below, so it is taken out of flow rather than given a
   zero-size box in the middle of the checklist. */
export const GradientDefsSvg = styled.svg`
    position: absolute;
`;

/* `@similarweb/icons` has no gradient-fill variant, so the checkmark's `<path>` is repainted
   via a shared SVG gradient def (rendered once by `GradientDefs`) referenced through `fill:
   url(#id)` — a CSS rule on the path wins over the icon's own inline `fill`/`currentColor`. */
export const GradientCheckIcon = styled.span`
    display: inline-flex;

    svg path {
        fill: url("#market-builder-v2-ai-gradient-check");
    }
`;

/* Indented to line up with the label text, past the 40px icon/loader slot (`StepIconSlot`) —
   not slot-width-plus-row-gap, just the slot's own 40px. Reused for every step that surfaces a
   list of items (discovered domains, created segments) so "what's happening under the hood"
   reads consistently. */
export const StepDetailList = styled(Stack)`
    margin-left: 40px;
    padding: 4px 0 8px;
    gap: 2px;
`;

/* Same indentation as `StepDetailList`, for steps whose live detail is a single line (the
   normalized topic, a rules-generation progress count) rather than a list of items. */
export const StepDetailCaption = styled.p`
    ${setFontVariant("body2", carbon[400])}
    margin: 4px 0 0 40px;
`;
